// Jurnal acțiuni (admin): fiecare rând = o acțiune pe platformă — cine, ce, cui și cu ce rezultat.
// Sus rândul comun (.pageToolbar): perioada, cine a făcut (căutarea), rezultatul; dedesubt, mereu
// deschis, al doilea rând cu listele (rolurile, tipul instructorului, ce, pe cine a atins, perioada
// aleasă) și „Resetează”. Totul se aplică singur: imediat la clic, după o pauză la scris. Dedesubt jurnalul ca un terminal (mereu negru, cu
// „Live” în bara lui): un clic pe un nume = doar acțiunile lui, pe un rând = detaliile cererii.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAuditLog, getAuditLogMeta } from "../../../api/auditLogService";
import {
   CATEGORY_LABELS,
   describeRow,
   INSTRUCTOR_TYPE_LABELS,
   LEVELS,
   levelOf,
   personLabel,
   presetRange,
   RESULT_LABELS,
   ROLE_LABELS,
   ROLE_SINGULAR,
   rowTitle,
   toLocalInput,
} from "../../../utils/auditLabels";
import { ButtonUI, DropdownUI, InputUI, SearchBarUI, StatusUI, TagUI } from "../../UI";

const PERIODS = [
   { value: "1h", label: "Ultima oră" },
   { value: "today", label: "Azi" },
   { value: "yesterday", label: "Ieri" },
   { value: "7d", label: "Ultimele 7 zile" },
   { value: "30d", label: "Ultimele 30 de zile" },
   { value: "all", label: "Toată perioada" },
   { value: "custom", label: "Aleg eu perioada…" },
];

const RESULTS = [{ value: "", label: "Toate rezultatele" }, ...Object.entries(RESULT_LABELS).map(([value, label]) => ({ value, label }))];
// în buton, scurt (lista are textul întreg)
const RESULT_SHORT = { "": "Rezultat", ok: "Reușite", error: "Erori", denied: "Refuzate" };


const EMPTY_FILTERS = {
   from: "",
   to: "",
   roles: [],
   instructorType: "",
   actor: "",
   actorId: "",
   subject: "",
   subjectUserId: "",
   categories: [],
   result: "",
   entityType: "",
   entityId: "",
};

const TYPING_DELAY_MS = 500; // textul se aplică după o jumătate de secundă fără taste
const LIVE_EVERY_MS = 5000;
const PAGE = 100;

// din câmpurile formularului -> parametrii cererii
function toRequest(f) {
   return {
      from: f.from ? new Date(f.from).toISOString() : undefined,
      to: f.to ? new Date(f.to).toISOString() : undefined,
      roles: f.roles,
      instructorType: f.instructorType || undefined,
      actor: f.actorId ? undefined : f.actor.trim() || undefined,
      actorId: f.actorId || undefined,
      subject: f.subjectUserId ? undefined : f.subject.trim() || undefined,
      subjectUserId: f.subjectUserId || undefined,
      categories: f.categories,
      result: f.result || undefined,
      entityType: f.entityType || undefined,
      entityId: f.entityId || undefined,
   };
}

const pad = (n) => String(n).padStart(2, "0");

function lineTime(value) {
   const d = new Date(value);
   return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const mergeNames = (a, b) => ({
   users: { ...(a?.users || {}), ...(b?.users || {}) },
   instructors: { ...(a?.instructors || {}), ...(b?.instructors || {}) },
});

const todayFilters = () => ({ ...EMPTY_FILTERS, from: toLocalInput(presetRange("today").from) });

export default function AuditLogPage() {
   const [meta, setMeta] = useState(null);
   const [filters, setFilters] = useState(todayFilters);
   const [period, setPeriod] = useState("today");
   const [items, setItems] = useState([]);
   const [names, setNames] = useState({ users: {}, instructors: {} });
   const [nextBeforeId, setNextBeforeId] = useState(null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState("");
   const [openId, setOpenId] = useState(null);
   const [live, setLive] = useState(true);
   const [freshIds, setFreshIds] = useState(() => new Set());
   const appliedRef = useRef(filters);
   const requestRef = useRef(0);
   const delayRef = useRef(0);
   const itemsRef = useRef(items);
   itemsRef.current = items;

   useEffect(() => {
      getAuditLogMeta()
         .then(setMeta)
         .catch((e) => setError(e.message));
   }, []);

   const load = useCallback(async (f, beforeId) => {
      const reqId = ++requestRef.current;
      setLoading(true);
      setError("");
      try {
         const res = await getAuditLog({ ...toRequest(f), beforeId, limit: PAGE });
         if (reqId !== requestRef.current) return; // a venit între timp o căutare mai nouă
         setItems((prev) => (beforeId ? [...prev, ...res.items] : res.items));
         setNames((prev) => (beforeId ? mergeNames(prev, res.names) : mergeNames(null, res.names)));
         setNextBeforeId(res.nextBeforeId);
         if (!beforeId) setFreshIds(new Set());
      } catch (e) {
         if (reqId === requestRef.current) setError(e.message);
      } finally {
         if (reqId === requestRef.current) setLoading(false);
      }
   }, []);

   // orice schimbare de filtru se aplică singură: imediat la clic, după o pauză la scris
   useEffect(() => {
      const t = setTimeout(() => {
         appliedRef.current = filters;
         setOpenId(null);
         load(filters);
      }, delayRef.current);
      return () => clearTimeout(t);
   }, [filters, load]);

   // Live: rândurile noi apar sus singure (doar când lista arată „până acum” și fila e deschisă)
   useEffect(() => {
      if (!live || filters.to) return undefined;
      const tick = async () => {
         if (document.hidden) return;
         const reqId = requestRef.current;
         try {
            const res = await getAuditLog({ ...toRequest(appliedRef.current), limit: PAGE });
            if (reqId !== requestRef.current) return; // între timp s-au schimbat filtrele
            const topId = itemsRef.current[0]?.id ?? 0;
            const fresh = res.items.filter((r) => r.id > topId);
            if (!fresh.length) return;
            setItems((prev) => [...fresh.filter((r) => !prev.some((p) => p.id === r.id)), ...prev]);
            setNames((prev) => mergeNames(prev, res.names));
            setFreshIds(new Set(fresh.map((r) => r.id)));
         } catch {
            // următoarea încercare peste câteva secunde
         }
      };
      const timer = setInterval(tick, LIVE_EVERY_MS);
      return () => clearInterval(timer);
   }, [live, filters.to]);

   const update = (patch, delay = 0) => {
      delayRef.current = delay;
      setFilters((f) => ({ ...f, ...patch }));
   };

   const choosePeriod = (id) => {
      setPeriod(id);
      if (id === "custom") return;
      const r = presetRange(id);
      update({ from: r.from ? toLocalInput(r.from) : "", to: r.to ? toLocalInput(r.to) : "" });
   };

   const reset = () => {
      setPeriod("today");
      delayRef.current = 0;
      setFilters(todayFilters());
   };

   const roles = meta?.roles || Object.keys(ROLE_LABELS);
   const categories = (meta?.categories || Object.keys(CATEGORY_LABELS)).filter((c) => c !== "audit");
   const instructorTypes = meta?.instructorTypes || Object.keys(INSTRUCTOR_TYPE_LABELS);

   const rows = useMemo(() => items.map((row) => ({ row, level: levelOf(row), text: describeRow(row, names) })), [items, names]);
   const counts = useMemo(() => {
      const c = { ok: 0, info: 0, warn: 0, err: 0 };
      rows.forEach((r) => {
         c[r.level] += 1;
      });
      return c;
   }, [rows]);

   const personName = (id) => names.users?.[id];
   const who = (id) => `#${id}${personName(id) ? ` · ${personName(id)}` : ""}`;

   // filtrul pe un obiect (ales din rândul unei acțiuni) n-are câmp în rândul filtrelor: îl arată o etichetă cu ×
   const entityChip =
      filters.entityType && filters.entityId ? `Obiect: ${filters.entityType} #${filters.entityId}` : "";
   const liveOn = live && !filters.to;

   // „Filtre”: rolurile și categoriile se bifează pe rând (lista se închide la fiecare alegere)
   const multiLabel = (picked, labels, all, many) =>
      !picked.length ? all : picked.length === 1 ? labels[picked[0]] || picked[0] : `${picked.length} ${many}`;
   const toggleIn = (list, value) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
   const roleOptions = [
      { value: "__all", label: "Toate rolurile", checked: !filters.roles.length },
      { divider: true },
      ...roles.map((r) => ({ value: r, label: ROLE_LABELS[r] || r, checked: filters.roles.includes(r) })),
   ];
   const categoryOptions = [
      { value: "__all", label: "Toate acțiunile", checked: !filters.categories.length },
      { divider: true },
      ...categories.map((c) => ({ value: c, label: CATEGORY_LABELS[c] || c, checked: filters.categories.includes(c) })),
   ];
   const typeOptions = [{ value: "", label: "Toate tipurile" }, ...instructorTypes.map((t) => ({ value: t, label: INSTRUCTOR_TYPE_LABELS[t] || t }))];

   return (
      <div className="adminPage auditPage">
         <div className="pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={period}
               onChange={choosePeriod}
               options={PERIODS}
               aria-label="Perioada"
               className="pageToolbar__menu"
            />
            <SearchBarUI
               surface="page"
               value={filters.actorId ? who(filters.actorId) : filters.actor}
               onChange={(e) => update({ actor: filters.actorId ? "" : e.target.value, actorId: "" }, TYPING_DELAY_MS)}
               onClear={() => update({ actor: "", actorId: "" })}
               placeholder="Cine a făcut: nume, email, telefon sau id…"
               aria-label="Cine a făcut"
               className="pageToolbar__search"
            />
            <div className="pageToolbar__controls">
               <DropdownUI
                  size="lg"
                  surface="page"
                  value={filters.result}
                  label={RESULT_SHORT[filters.result] || "Rezultat"}
                  onChange={(v) => update({ result: v })}
                  options={RESULTS}
                  aria-label="Rezultatul"
               />
            </div>
         </div>

         {/* filtrele: al doilea rând, mereu deschis, cu listele în rând */}
         <div className="pageToolbar auditPage__row2">
               <DropdownUI
                  size="lg"
                  surface="page"
                  options={roleOptions}
                  label={multiLabel(filters.roles, ROLE_LABELS, "Toate rolurile", "roluri")}
                  onChange={(v) => update({ roles: v === "__all" ? [] : toggleIn(filters.roles, v) })}
                  aria-label="Cine a făcut (rolul)"
               />
               <DropdownUI
                  size="lg"
                  surface="page"
                  value={filters.instructorType}
                  label={filters.instructorType ? INSTRUCTOR_TYPE_LABELS[filters.instructorType] || filters.instructorType : "Tip instructor"}
                  onChange={(v) => update({ instructorType: v })}
                  options={typeOptions}
                  aria-label="Tipul instructorului"
               />
               <DropdownUI
                  size="lg"
                  surface="page"
                  options={categoryOptions}
                  label={multiLabel(filters.categories, CATEGORY_LABELS, "Toate acțiunile", "categorii")}
                  onChange={(v) => update({ categories: v === "__all" ? [] : toggleIn(filters.categories, v) })}
                  aria-label="Ce fel de acțiuni"
               />
               <SearchBarUI
                  surface="page"
                  value={filters.subjectUserId ? who(filters.subjectUserId) : filters.subject}
                  onChange={(e) => update({ subject: filters.subjectUserId ? "" : e.target.value, subjectUserId: "" }, TYPING_DELAY_MS)}
                  onClear={() => update({ subject: "", subjectUserId: "" })}
                  placeholder="Pe cine a atins: elevul sau persoana…"
                  aria-label="Pe cine a atins"
                  className="auditPage__subject"
               />
               {period === "custom" ? (
                  <>
                     <InputUI
                        type="datetime-local"
                        label="De la"
                        value={filters.from}
                        onChange={(e) => update({ from: e.target.value }, TYPING_DELAY_MS)}
                        className="auditPage__date"
                     />
                     <InputUI
                        type="datetime-local"
                        label="Până la"
                        value={filters.to}
                        onChange={(e) => update({ to: e.target.value }, TYPING_DELAY_MS)}
                        className="auditPage__date"
                     />
                  </>
               ) : null}
               {entityChip ? (
                  <TagUI size="md" selected onClick={() => update({ entityType: "", entityId: "" })} title="Scoate filtrul" className="auditPage__entity">
                     {entityChip} ×
                  </TagUI>
               ) : null}
               <ButtonUI size="lg" variant="subtle" onClick={reset}>
                  Resetează
               </ButtonUI>
         </div>

         {meta && meta.enabled === false ? (
            <StatusUI look="card" tone="warning">
               Jurnalul este oprit pe server (AUDIT_LOG=off).
            </StatusUI>
         ) : null}

         {/* jurnalul, ca un terminal (mereu negru, cu marginea de sticlă) */}
         <div className="auditTerm auditPage__term">
            <div className="auditTerm__bar">
               <span className="auditTerm__dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
               </span>
               <span className="auditTerm__title">
                  jurnal · {items.length}
                  {nextBeforeId ? "+" : ""} {items.length === 1 ? "rând" : "rânduri"}
                  {loading ? " · se încarcă…" : ""}
               </span>
               <span className="auditTerm__counts">
                  {counts.err > 0 && <span className="is-err">{counts.err} ERR</span>}
                  {counts.warn > 0 && <span className="is-warn">{counts.warn} WARN</span>}
                  {counts.ok > 0 && <span className="is-ok">{counts.ok} OK</span>}
               </span>
               <button
                  type="button"
                  className={`auditTerm__live${liveOn ? " is-on" : ""}`}
                  onClick={() => setLive((v) => !v)}
                  disabled={!!filters.to}
                  title={filters.to ? "Live merge doar fără „Până la”" : "Rândurile noi apar singure, la fiecare 5 secunde"}
               >
                  <span className="auditTerm__liveDot" /> Live
               </button>
            </div>

            <div className="auditTerm__legend">
               {Object.entries(LEVELS).map(([id, l]) => (
                  <span key={id} className={`is-${id}`}>
                     <b>{l.tag}</b> {l.label}
                  </span>
               ))}
            </div>

            {error && <div className="auditTerm__line is-err auditTerm__line--static">eroare: {error}</div>}

            <div className="auditTerm__body" role="log" aria-label="Jurnal acțiuni">
               {rows.map(({ row, level, text }) => {
                  const open = openId === row.id;
                  const toggleOpen = () => setOpenId(open ? null : row.id);
                  const stop = (fn) => (e) => {
                     e.stopPropagation();
                     fn();
                  };
                  return (
                     <div
                        key={row.id}
                        className={`auditTerm__line is-${level}${open ? " is-open" : ""}${freshIds.has(row.id) ? " is-fresh" : ""}`}
                        role="button"
                        tabIndex={0}
                        onClick={toggleOpen}
                        onKeyDown={(e) => {
                           if (e.target !== e.currentTarget) return;
                           if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleOpen();
                           }
                        }}
                     >
                        <span className="auditTerm__time">{lineTime(row.createdAt)}</span>
                        <span className="auditTerm__lvl">{LEVELS[level].tag}</span>
                        <span className="auditTerm__cat">{CATEGORY_LABELS[row.category] || row.category}</span>
                        <span className="auditTerm__msg">
                           {row.actorId ? (
                              <button
                                 type="button"
                                 className="auditTerm__who"
                                 title="Doar acțiunile acestei persoane"
                                 onClick={stop(() => update({ actorId: String(row.actorId), actor: "" }))}
                              >
                                 {personLabel(row.actor, row.actorId)}
                              </button>
                           ) : (
                              <span className="auditTerm__who is-anon">{row.details?.email || "anonim"}</span>
                           )}
                           {row.actorRole && <span className="auditTerm__role">[{ROLE_SINGULAR[row.actorRole] || row.actorRole}]</span>}{" "}
                           <span className="auditTerm__action">{rowTitle(row)}</span>
                           {row.subjectUserId && row.subjectUserId !== row.actorId && (
                              <>
                                 {" → "}
                                 <button
                                    type="button"
                                    className="auditTerm__who"
                                    title="Tot ce s-a făcut cu această persoană"
                                    onClick={stop(() => update({ subjectUserId: String(row.subjectUserId), subject: "" }))}
                                 >
                                    {personLabel(row.subject, row.subjectUserId)}
                                 </button>
                                 {row.subject?.role && <span className="auditTerm__role">[{ROLE_SINGULAR[row.subject.role] || row.subject.role}]</span>}
                              </>
                           )}
                           {text && <span className="auditTerm__text"> · {text}</span>}
                           <span className="auditTerm__meta">
                              {" "}
                              {row.statusCode} · {row.durationMs ?? "?"} ms
                           </span>
                        </span>

                        {open && (
                           <div className="auditTerm__details" onClick={(e) => e.stopPropagation()}>
                              <div>
                                 ↳ {row.method} {row.route} · {row.statusCode} · {row.durationMs ?? "?"} ms
                                 {row.ip ? ` · IP ${row.ip}` : ""}
                                 {row.entityType && row.entityId && (
                                    <>
                                       {" · obiect "}
                                       <button
                                          type="button"
                                          className="auditTerm__who"
                                          title="Tot istoricul acestui obiect"
                                          onClick={() => {
                                             setPeriod("all");
                                             update({ entityType: row.entityType, entityId: row.entityId, from: "", to: "" });
                                          }}
                                       >
                                          {row.entityType} #{row.entityId}
                                       </button>
                                    </>
                                 )}
                              </div>
                              {row.userAgent && <div className="auditTerm__ua">↳ {row.userAgent}</div>}
                              {(row.details || row.lesson) && (
                                 <pre className="auditTerm__pre">
                                    {JSON.stringify({ ...(row.details || {}), ...(row.lesson ? { lesson: row.lesson } : {}) }, null, 2)}
                                 </pre>
                              )}
                           </div>
                        )}
                     </div>
                  );
               })}
               {!loading && !items.length && !error && (
                  <div className="auditTerm__line is-dim auditTerm__line--static">— nimic pentru filtrele alese —</div>
               )}
               {nextBeforeId && (
                  <button type="button" className="auditTerm__more" disabled={loading} onClick={() => load(appliedRef.current, nextBeforeId)}>
                     ↓ mai vechi
                  </button>
               )}
            </div>
         </div>
      </div>
   );
}
